import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import Level1Badge from './BranchLogo_DarkPurple-tier-1.png';
import Level2Badge from './BranchLogo_DarkPurple-tier-2.png';
import Level3Badge from './BranchLogo_DarkPurple-tier-3.png';
import { Level4BadgeSvg } from './Level4BadgeSvg';

const RESEARCH_RELATED_PERMISSIONS = ['researcher', 'reviewer', 'publisher', 'editor', 'admin'];
const PUBLISHER_OR_HIGHER = ['publisher', 'editor', 'admin', 'super-admin'];

export interface ResearcherLevelBadgeProps {
  researcherLevel?: number;
  publisherLevel?: number;
  permissions?: string[];
  /** Legacy: force display level when provided. */
  level?: number;
  style?: React.CSSProperties;
  className?: string;
}

interface GetDisplayLevelParams {
  researcherLevel?: number;
  publisherLevel?: number;
  permissions?: string[];
}

/**
 * Derives display level from user context.
 * - No research-related permissions → null (don't render).
 * - Publisher or higher (by permission) → 4.
 * - publisherLevel === 3 → 4.
 * - Otherwise → researcherLevel (1, 2, or 3).
 */
export function getDisplayLevel({
  researcherLevel,
  publisherLevel,
  permissions = [],
}: GetDisplayLevelParams): number | null {
  const perms = permissions || [];
  const hasResearchPermission = perms.some((p) =>
    RESEARCH_RELATED_PERMISSIONS.includes(p)
  );
  if (!hasResearchPermission) return null;

  const isPublisherOrHigher = perms.some((p) =>
    PUBLISHER_OR_HIGHER.includes(p)
  );
  if (isPublisherOrHigher) return 4;
  if (publisherLevel === 3) return 4;

  const level = researcherLevel ?? 1;
  if (level >= 1 && level <= 4) return level;
  return null;
}

const ResearcherLevelBadge: React.FC<ResearcherLevelBadgeProps> = ({
  researcherLevel,
  publisherLevel,
  permissions,
  level: levelProp,
  style = {},
  className = '',
}) => {
  const level =
    levelProp != null
      ? levelProp
      : getDisplayLevel({ researcherLevel, publisherLevel, permissions });
  if (level == null) return null;

  if (level === 4) {
    return (
      <Tooltip title={`Level 4`}>
        <Level4BadgeSvg color="#876ce9" style={style} className={className} />
      </Tooltip>
    );
  }

  let badge: string;
  if (level === 1) badge = Level1Badge;
  else if (level === 2) badge = Level2Badge;
  else if (level === 3) badge = Level3Badge;
  else return null;

  return (
    <Tooltip title={`Level ${level}`}>
      <Badge src={badge} alt={`Level ${level}`} style={style} className={className} />
    </Tooltip>
  );
};

const Badge = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

export { ResearcherLevelBadge };
